import React, { useState } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import GoogleIcon from '@mui/icons-material/Google';
import { isNativeApp } from '../utils/platform';

const SocialLogin = ({ isSignup }) => {
  const googleId = process.env.REACT_APP_GOOGLE_ID;
  const API_BASE_URL = process.env.REACT_APP_BASE_URL;
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  if (!googleId) {
    console.error('REACT_APP_GOOGLE_ID is not set in your environment variables.');
    return null;
  }

  // Exchange Google tokens with the backend and persist the JWT pair.
  // Shared by the web (@react-oauth/google) and native (Capacitor) flows.
  const exchangeAndPersist = async ({ credential, baseUrl }) => {
    const url = `${baseUrl || ''}api/v1/auth/google/`;
    // Send ONLY the id_token (as access_token + id_token). Do NOT send `code`:
    // dj-rest-auth only takes allauth's id_token verification path when no code
    // is present. Forwarding the native serverAuthCode pushes it into a
    // code-exchange branch that crashes (response.get on a str) in allauth's
    // Google adapter. Web works because its credential response has no code.
    const result = await axios.post(url, {
      access_token: credential,
      id_token: credential,
    });
    if (result.status === 200) {
      const data = result.data;
      localStorage.setItem('aircheck_access', data.access);
      localStorage.setItem('aircheck_refresh', data.refresh);
      const from = location.state?.from || '/';
      navigate(from);
    }
  };

  const reportError = (err) => {
    console.error('Error during Google authentication:', err.response ? err.response.data : err.message);
    setError(
      Object.values(err.response ? err.response.data : { msg: err.message }).join(', '),
    );
  };

  // --- Web flow: @react-oauth/google (browser only; Google blocks it in WebViews) ---
  const handleWebSuccess = async (response) => {
    try {
      const { credential } = response;
      // Web build is served same-origin as the backend, so a relative URL works.
      await exchangeAndPersist({ credential });
    } catch (err) {
      reportError(err);
    }
  };

  const handleWebFailure = (err) => {
    console.error('Google Login failed:', err);
    setError('Google Login failed');
  };

  // --- Native flow: Capacitor Google Auth plugin (real native account picker) ---
  const handleNativeGoogle = async () => {
    setError('');
    try {
      const result = await GoogleAuth.signIn();
      const idToken = result?.authentication?.idToken;
      // Bundled app runs at https://localhost, so the backend URL must be absolute.
      await exchangeAndPersist({ credential: idToken, baseUrl: `${API_BASE_URL}/` });
    } catch (err) {
      reportError(err);
    }
  };

  if (isNativeApp()) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
        {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
        <Button
          onClick={handleNativeGoogle}
          variant="outlined"
          fullWidth
          size="large"
          startIcon={<GoogleIcon />}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {isSignup ? 'Sign up with Google' : 'Continue with Google'}
        </Button>
      </Box>
    );
  }

  return (
    <GoogleOAuthProvider clientId={googleId}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
        <GoogleLogin
          onSuccess={handleWebSuccess}
          onError={handleWebFailure}
          responseType="code"
          prompt="consent"
          text="continue_with"
          useOneTap
        />
      </Box>
    </GoogleOAuthProvider>
  );
};

export default SocialLogin;
